import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRole } from '@/composables/useRole'
import { getPostLoginPath } from '@/utils/loginRedirect'

// Extend route meta interface
declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    requiresAdmin?: boolean
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      redirect: '/dashboard'
    },
    {
      path: '/login',
      name: 'Login',
      component: () => import('@/views/LoginView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () => import('@/views/DashboardView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/sessions',
      name: 'Sessions',
      component: () => import('@/views/SessionsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/sessions/:id',
      name: 'SessionDetail',
      component: () => import('@/views/SessionDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/employees',
      name: 'Employees',
      component: () => import('@/views/EmployeesView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/employees/:id',
      name: 'EmployeeDetail',
      component: () => import('@/views/EmployeeDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/mappings',
      name: 'mappings',
      component: () => import('@/views/FieldMappingsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/mappings/:id',
      name: 'mapping-detail',
      component: () => import('@/views/FieldMappingDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/errors',
      name: 'SystemErrors',
      component: () => import('@/views/SystemErrorsView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/error',
      name: 'Error',
      component: () => import('@/views/ErrorView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/system-info',
      name: 'SystemInfo',
      component: () => import('@/views/SystemInfoView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/configuration',
      name: 'Configuration',
      component: () => import('@/views/ConfigurationView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/user-action-logs',
      name: 'UserActionLogs',
      component: () => import('@/views/UserActionLogsView.vue'),
      meta: { requiresAuth: true, requiresAdmin: true }
    },
    {
      path: '/forbidden',
      name: 'Forbidden',
      component: () => import('@/views/ForbiddenView.vue'),
      meta: { requiresAuth: false }
    },
    {
      path: '/exports',
      name: 'Exports',
      component: () => import('@/views/ExportView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/exports/:id',
      name: 'ExportDetail',
      component: () => import('@/views/ExportDetailView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/help/:slug?',
      name: 'Help',
      component: () => import('@/views/HelpView.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('@/views/NotFoundView.vue'),
      meta: { requiresAuth: false }
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Initialize auth if not already done
  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  const requiresAuth = to.meta.requiresAuth !== false
  const requiresAdmin = to.meta.requiresAdmin === true
  const isAuthenticated = authStore.isAuthenticated

  if (requiresAuth && !isAuthenticated) {
    // Redirect to login if authentication is required but user is not authenticated
    next({ path: '/login', query: { redirect: to.fullPath } })
  } else if (to.path === '/login' && isAuthenticated) {
    // Redirect back to the requested page (or dashboard) if user is already authenticated
    next(getPostLoginPath(to.query.redirect))
  } else if (requiresAdmin && isAuthenticated) {
    // Check admin role for admin-only routes
    const { isAdmin } = useRole()
    if (!isAdmin.value) {
      // User is authenticated but not an admin
      next('/forbidden')
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
